import { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, fmtErr } from "@/lib/api";
import { toast } from "sonner";
import { X, RefreshCw, Trash2 } from "lucide-react";

const StatusBadge = ({ s }) => {
  const map = {
    queued: "bg-slate-100 text-slate-700",
    processing: "bg-cyan-100 text-cyan-800",
    completed: "bg-emerald-100 text-emerald-800",
    partial: "bg-amber-100 text-amber-800",
    failed: "bg-red-100 text-red-800",
  };
  return <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md ${map[s] || "bg-slate-100"}`}>{s}</span>;
};

const LogStatus = ({ s }) => {
  const map = {
    queued: "text-emerald-700",
    failed: "text-red-700",
  };
  return <span className={`tiny-label ${map[s] || ""}`}>{s}</span>;
};

export default function AdminJobs() {
  const [jobs, setJobs] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/jobs");
    setJobs(data);
    // prune selection of ids no longer present
    setSelected((cur) => new Set([...cur].filter((id) => data.some((j) => j.id === id))));
  };
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, []);

  const openDetail = async (id) => {
    setLoadingDetail(true);
    try {
      const { data } = await api.get(`/admin/jobs/${id}`);
      setDetail(data);
    } finally {
      setLoadingDetail(false);
    }
  };

  const toggle = (id) => {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allChecked = jobs.length > 0 && selected.size === jobs.length;
  const someChecked = selected.size > 0 && selected.size < jobs.length;
  const toggleAll = () => {
    if (allChecked) setSelected(new Set());
    else setSelected(new Set(jobs.map((j) => j.id)));
  };

  const deleteOne = async (id, e) => {
    e?.stopPropagation();
    if (!window.confirm("Bu SMS işi ve tüm log kayıtları silinsin mi?")) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/jobs/${id}`);
      toast.success("İş silindi");
      setSelected((cur) => { const n = new Set(cur); n.delete(id); return n; });
      if (detail?.job?.id === id) setDetail(null);
      load();
    } catch (e) {
      toast.error(fmtErr(e?.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  const deleteSelected = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`${selected.size} adet SMS işi (ve logları) silinecek. Emin misiniz?`)) return;
    setDeleting(true);
    try {
      const { data } = await api.post("/admin/jobs/bulk-delete", { ids: [...selected] });
      toast.success(`${data.jobs_deleted} iş · ${data.logs_deleted} log silindi`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(fmtErr(e?.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AdminLayout>
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="tiny-label mb-2">İzleme</div>
          <h1 className="font-display font-black text-4xl text-slate-900">SMS Logları</h1>
          <p className="text-sm text-slate-600 mt-2">Tüm müşterilerin SMS gönderim işleri ve alıcı detayları.</p>
        </div>
        <div className="flex items-center gap-2">
          {selected.size > 0 && (
            <button
              onClick={deleteSelected}
              disabled={deleting}
              data-testid="bulk-delete-btn"
              className="flex items-center gap-2 text-sm font-semibold px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              <Trash2 className="w-4 h-4" /> Sil ({selected.size})
            </button>
          )}
          <button onClick={load} className="flex items-center gap-2 text-sm font-semibold px-3 py-2 border border-turq-200 text-turq-800 rounded-lg hover:bg-turq-50" data-testid="refresh-jobs">
            <RefreshCw className="w-4 h-4" /> Yenile
          </button>
        </div>
      </div>

      <div className="card-soft overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50/50">
            <tr className="text-left border-b border-turq-100">
              <th className="px-4 py-3 w-10">
                <input
                  type="checkbox"
                  checked={allChecked}
                  ref={(el) => { if (el) el.indeterminate = someChecked; }}
                  onChange={toggleAll}
                  data-testid="select-all"
                  className="w-4 h-4 accent-turq-600 cursor-pointer"
                />
              </th>
              <th className="px-5 py-3 tiny-label">Tarih</th>
              <th className="px-5 py-3 tiny-label">Müşteri</th>
              <th className="px-5 py-3 tiny-label">Mod</th>
              <th className="px-5 py-3 tiny-label">Toplam</th>
              <th className="px-5 py-3 tiny-label">İlerleme</th>
              <th className="px-5 py-3 tiny-label">Zamanlanmış</th>
              <th className="px-5 py-3 tiny-label">Durum</th>
              <th className="px-5 py-3 w-12"></th>
            </tr>
          </thead>
          <tbody>
            {jobs.length === 0 && <tr><td colSpan={9} className="px-5 py-10 text-center text-slate-400">Henüz iş yok</td></tr>}
            {jobs.map((j) => {
              const pct = j.total > 0 ? Math.round((j.processed / j.total) * 100) : 0;
              const isSel = selected.has(j.id);
              return (
                <tr key={j.id} className={`border-b border-slate-100 hover:bg-turq-50/40 cursor-pointer ${isSel ? "bg-turq-50/60" : ""}`} data-testid={`job-row-${j.id}`}>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={isSel}
                      onChange={() => toggle(j.id)}
                      data-testid={`select-${j.id}`}
                      className="w-4 h-4 accent-turq-600 cursor-pointer"
                    />
                  </td>
                  <td className="px-5 py-3 font-mono text-xs whitespace-nowrap" onClick={() => openDetail(j.id)}>{new Date(j.created_at).toLocaleString("tr-TR")}</td>
                  <td className="px-5 py-3 font-semibold text-slate-900" onClick={() => openDetail(j.id)}>{j.customer_name}</td>
                  <td className="px-5 py-3" onClick={() => openDetail(j.id)}><span className="tiny-label">{j.mode}</span></td>
                  <td className="px-5 py-3 font-mono" onClick={() => openDetail(j.id)}>{j.total}</td>
                  <td className="px-5 py-3 w-56" onClick={() => openDetail(j.id)}>
                    <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                      <div className="bg-gradient-to-r from-turq-600 to-cyan-500 h-2 transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="text-[11px] mt-1 font-mono">
                      <span className="text-emerald-700">{j.success_count}</span> / <span className="text-red-700">{j.fail_count}</span> · {pct}%
                    </div>
                  </td>
                  <td className="px-5 py-3 font-mono text-xs" onClick={() => openDetail(j.id)}>{j.scheduled_at ? new Date(j.scheduled_at).toLocaleString("tr-TR") : "—"}</td>
                  <td className="px-5 py-3" onClick={() => openDetail(j.id)}><StatusBadge s={j.status} /></td>
                  <td className="px-5 py-3">
                    <button
                      onClick={(e) => deleteOne(j.id, e)}
                      disabled={deleting}
                      data-testid={`delete-job-${j.id}`}
                      title="Sil"
                      className="p-1.5 text-slate-400 hover:text-red-600 disabled:opacity-40"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Detail panel */}
      {detail && (
        <div className="fixed inset-0 z-50 flex">
          <div className="flex-1 bg-slate-900/30 backdrop-blur-sm" onClick={() => setDetail(null)} />
          <div className="w-full max-w-2xl bg-white border-l border-turq-100 overflow-y-auto" data-testid="job-detail">
            <div className="p-6 border-b border-turq-100 flex items-start justify-between sticky top-0 bg-white z-10">
              <div>
                <div className="tiny-label">İş Detayı</div>
                <h3 className="font-display font-bold text-2xl text-slate-900 mt-1 font-mono">{detail.job.id.slice(0, 8)}…</h3>
                <div className="mt-3 grid grid-cols-3 gap-4 text-sm">
                  <div><div className="tiny-label">Toplam</div><div className="font-mono font-bold">{detail.job.total}</div></div>
                  <div><div className="tiny-label">Başarı</div><div className="font-mono font-bold text-emerald-700">{detail.job.success_count}</div></div>
                  <div><div className="tiny-label">Hata</div><div className="font-mono font-bold text-red-700">{detail.job.fail_count}</div></div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => deleteOne(detail.job.id)} className="p-2 text-red-500 hover:bg-red-50 hover:text-red-700 rounded-lg" title="İşi sil" data-testid="delete-job-detail">
                  <Trash2 className="w-4 h-4" />
                </button>
                <button onClick={() => setDetail(null)} className="p-2 hover:bg-turq-50 rounded-lg"><X className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="px-6 py-4 border-b border-turq-100 bg-turq-50/40">
              <div className="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <div className="tiny-label mb-1">Mod</div>
                  <div className="font-mono text-xs">{detail.job.mode}</div>
                </div>
                <div>
                  <div className="tiny-label mb-1">Originator</div>
                  <div className="font-mono text-xs">{detail.job.originator}</div>
                </div>
                <div>
                  <div className="tiny-label mb-1">Oluşturuldu</div>
                  <div className="font-mono text-xs">{new Date(detail.job.created_at).toLocaleString("tr-TR")}</div>
                </div>
                <div>
                  <div className="tiny-label mb-1">Bitti</div>
                  <div className="font-mono text-xs">{detail.job.finished_at ? new Date(detail.job.finished_at).toLocaleString("tr-TR") : "—"}</div>
                </div>
                {detail.job.scheduled_at && (
                  <div className="col-span-2">
                    <div className="tiny-label mb-1">Zamanlanmış (STARTDATE)</div>
                    <div className="font-mono text-xs">{new Date(detail.job.scheduled_at).toLocaleString("tr-TR")} <span className="text-slate-400">(api: {detail.job.api_start_date})</span></div>
                  </div>
                )}
                {detail.job.api_expire_date && (
                  <div className="col-span-2">
                    <div className="tiny-label mb-1">Zaman Aşımı (EXPIREDATE)</div>
                    <div className="font-mono text-xs">
                      {detail.job.expire_at
                        ? new Date(detail.job.expire_at).toLocaleString("tr-TR")
                        : "Otomatik (STARTDATE + 24 saat)"}
                      <span className="text-slate-400"> (api: {detail.job.api_expire_date})</span>
                    </div>
                  </div>
                )}
              </div>
              {detail.job.last_gateway_response && (
                <div>
                  <div className="tiny-label mb-1">Son Gateway Yanıtı</div>
                  <pre className={`text-[11px] font-mono p-2.5 rounded-md border whitespace-pre-wrap break-all ${detail.job.status === "failed" || detail.job.status === "partial" ? "bg-red-50 text-red-800 border-red-200" : "bg-emerald-50 text-emerald-800 border-emerald-200"}`}>
{detail.job.last_gateway_response}
                  </pre>
                  {detail.job.last_endpoint && <div className="text-[10px] text-slate-500 mt-1 font-mono">↳ {detail.job.last_endpoint}</div>}
                </div>
              )}
              {detail.job.last_payload && (
                <div>
                  <div className="tiny-label mb-1 flex items-center justify-between">
                    <span>Gateway&apos;e Gönderilen XML</span>
                    <span className="text-[10px] text-slate-400 normal-case font-sans">(şifre maskelendi)</span>
                  </div>
                  <pre className="text-[10px] font-mono p-2.5 rounded-md border bg-slate-900 text-slate-100 border-slate-700 whitespace-pre-wrap break-all max-h-48 overflow-y-auto" data-testid="job-xml-payload">
{detail.job.last_payload}
                  </pre>
                </div>
              )}
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-left border-b border-turq-100">
                  <th className="px-6 py-2 tiny-label">Telefon</th>
                  <th className="px-6 py-2 tiny-label">Mesaj</th>
                  <th className="px-6 py-2 tiny-label">Durum</th>
                  <th className="px-6 py-2 tiny-label">Gateway</th>
                </tr>
              </thead>
              <tbody>
                {detail.logs.map((l) => (
                  <tr key={l.id} className="border-b border-slate-100" data-testid={`log-${l.id}`}>
                    <td className="px-6 py-2 font-mono text-xs">{l.phone}</td>
                    <td className="px-6 py-2 text-xs max-w-xs truncate" title={l.message}>{l.message}</td>
                    <td className="px-6 py-2"><LogStatus s={l.status} /></td>
                    <td className="px-6 py-2 text-[11px] font-mono max-w-[160px] truncate" title={l.gateway_response}>{l.gateway_response || "—"}</td>
                  </tr>
                ))}
                {detail.logs.length === 0 && (
                  <tr><td colSpan={4} className="px-6 py-10 text-center text-slate-400">Henüz log yok</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {loadingDetail && <div className="fixed bottom-6 right-6 btn-primary text-xs px-4 py-2.5 rounded-xl">Yükleniyor…</div>}
    </AdminLayout>
  );
}
